// =============================================================================
// PEDRA — src/components/UnfitPanel.tsx
// Pecas que nao couberam em nenhuma chapa, com o motivo em pt-BR e uma
// sugestao pratica de correcao para cada caso.
//
// Vive no cartao vermelho "Peças em falta" da coluna da direita, entao a
// apresentacao e' uma LISTA empilhada (nao uma tabela larga): cada item traz
// a peca, a quantidade que ficou de fora, o motivo e como resolver.
// =============================================================================

import type { ReactElement } from 'react';
import type { Settings, UnfitPart, UnfitReason } from '../core/types';
import { formatDimensions } from '../core/units';
import { formatCount, unfitReasonLabel } from '../lib/format';

export interface UnfitPanelProps {
  unfit: readonly UnfitPart[];
  settings: Settings;
}

/** O que o usuario pode fazer para resolver cada motivo. */
const FIX_HINTS: Readonly<Record<UnfitReason, string>> = {
  tooLarge: 'Cadastre uma chapa maior ou reduza as medidas da peça.',
  grainConflict: 'Desligue "Considerar o veio" ou ajuste o veio da peça ou da chapa.',
  noMaterialStock: 'Cadastre uma chapa com esse material ou desligue "Considerar materiais".',
  stockExhausted: 'Aumente a quantidade disponível no estoque de chapas.',
  invalid: 'Revise o comprimento, a largura e a quantidade dessa linha.',
};

export function UnfitPanel(props: UnfitPanelProps): ReactElement {
  const { unfit, settings } = props;

  if (unfit.length === 0) {
    return <p className="unfit-ok">Todas as peças couberam nas chapas disponíveis.</p>;
  }

  const totalQty = unfit.reduce((acc, u) => acc + u.qty, 0);

  return (
    <div className="unfit-root" role="region" aria-label="Peças que não couberam">
      <p className="unfit-sub">
        {`${formatCount(totalQty)} ${totalQty === 1 ? 'peça ficou' : 'peças ficaram'} de fora. ` +
          'Ajuste as peças, o estoque ou as configurações e calcule novamente.'}
      </p>

      <ul className="unfit-list">
        {unfit.map((u, i) => (
          <li className="unfit-item" key={`${u.partId}-${i}`}>
            <div className="unfit-item-head">
              <span className="unfit-item-name truncate">{u.label || `Peça ${i + 1}`}</span>
              <span className="unfit-item-qty tabular">{`${formatCount(u.qty)}×`}</span>
            </div>
            <div className="unfit-item-dims tabular">
              {formatDimensions(
                u.length,
                u.width,
                settings.unit,
                settings.decimalPlaces,
                settings.dimensionOrder,
              )}
              {u.material ? ` · ${u.material}` : ''}
            </div>
            <div className="unfit-reason">{unfitReasonLabel(u.reason)}</div>
            <div className="unfit-message">{u.message}</div>
            <div className="unfit-fix">{FIX_HINTS[u.reason]}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default UnfitPanel;
